import app from 'flarum/forum/app';
import extractText from 'flarum/common/utils/extractText';
import DeckColumns from 'ext:flarum/deck/forum/extenders/DeckColumns';
import DiscussionListSource from 'ext:flarum/deck/forum/columns/DiscussionListSource';
import PostListSource from 'ext:flarum/deck/forum/columns/PostListSource';
/** Broadcast through flarum/realtime when a best answer is set or removed (see extend.php). */
export const BEST_ANSWER_EVENT = 'bestAnswerEvent';
const hasQnATags = () => app.store.all('tags').some(tag => tag.isQnA());

/** From the post's own data, so it works without the discussion loaded. */
const discussionIdOf = post => {
  var _post$data;
  return (_post$data = post.data) == null || (_post$data = _post$data.relationships) == null || (_post$data = _post$data.discussion) == null || (_post$data = _post$data.data) == null ? void 0 : _post$data.id;
};

/** `hasBestAnswer` holds the best answer's post id. */
const isBestAnswer = (post, discussion) => String(discussion.hasBestAnswer()) === post.id();
const isQuestion = discussion => {
  const tags = discussion.tags();
  return Array.isArray(tags) && tags.some(tag => tag == null ? void 0 : tag.isQnA());
};

/**
 * Deck's classes only exist while it's enabled, so the sources extending them
 * are declared when this runs, not when the module loads.
 */
export default function deckColumns() {
  class UnsolvedQuestionsSource extends DiscussionListSource {
    constructor() {
      super({
        filter: {
          qna: true,
          '-solved-discussions': true
        }
      }, discussion => !discussion.hasBestAnswer());
    }
    onRealtime(event) {
      const discussion = event.discussion;
      if (event.name !== BEST_ANSWER_EVENT || !discussion) return super.onRealtime(event);
      const shown = this.shows(discussion);
      if (discussion.hasBestAnswer()) {
        if (!shown) return;
        this.removeFromList(discussion);
        return 'updated';
      }
      if (shown) return 'updated';

      // Reopened, it belongs here again, though its last activity may be older than anything shown.
      if (isQuestion(discussion)) {
        this.moveToTop(discussion);
        return 'inserted';
      }
    }
  }
  class BestAnswersSource extends PostListSource {
    constructor() {
      super({
        filter: {
          solution: true
        }
      });
    }

    // A new post is never already a best answer.
    matches() {
      return false;
    }
    mayMatchNewDiscussion() {
      return false;
    }
    onRealtime(event) {
      const discussion = event.discussion;
      if (event.name !== BEST_ANSWER_EVENT || !discussion) return super.onRealtime(event);

      // A discussion has one best answer, so any other of its posts shown here was replaced or removed.
      const replaced = this.shownPosts().filter(post => discussionIdOf(post) === discussion.id() && !isBestAnswer(post, discussion));
      replaced.forEach(post => this.removePost(post.id()));
      const answer = discussion.hasBestAnswer() ? app.store.getById('posts', String(discussion.hasBestAnswer())) : undefined;
      if (answer && this.insert(answer)) return 'inserted';
      return replaced.length ? 'updated' : undefined;
    }
    prune() {
      const stale = this.shownPosts().filter(post => {
        var _discussionIdOf;
        const discussion = app.store.getById('discussions', (_discussionIdOf = discussionIdOf(post)) != null ? _discussionIdOf : '');
        return discussion && !isBestAnswer(post, discussion);
      });
      if (!stale.length) return;
      stale.forEach(post => this.removePost(post.id()));
      this.refill();
    }
    shownPosts() {
      return this.state.getPages().flatMap(page => page.items);
    }
  }
  return new DeckColumns().add('fof-best-answer-unsolved', {
    icon: 'far fa-circle-question',
    label: () => extractText(app.translator.trans('fof-best-answer.forum.deck.unsolved_questions')),
    title: () => extractText(app.translator.trans('fof-best-answer.forum.deck.unsolved_questions')),
    isAvailable: hasQnATags,
    createSource: () => new UnsolvedQuestionsSource()
  }, 34).add('fof-best-answer-best-answers', {
    icon: 'fas fa-check',
    label: () => extractText(app.translator.trans('fof-best-answer.forum.deck.best_answers')),
    title: () => extractText(app.translator.trans('fof-best-answer.forum.deck.best_answers')),
    isAvailable: hasQnATags,
    createSource: () => new BestAnswersSource()
  }, 33);
}
flarum.reg.add('fof-best-answer', 'forum/deck/deckColumns', deckColumns);